import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import MembersList from './MembersList';
import { MdArrowBack } from 'react-icons/md';

const Wings = ({ onBack, onEditClick, onMemberClick }) => {
    const [selectedWing, setSelectedWing] = useState(null);
    const [activeWings, setActiveWings] = useState([]);
    const [inactiveWings, setInactiveWings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // New Wing Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [newWingName, setNewWingName] = useState('');
    const [addingWing, setAddingWing] = useState(false);

    const fetchWingsData = async () => {
        setLoading(true);
        try {
            const response = await axios.get('nai-members/wings/summary/');
            setActiveWings(response.data.active || []);
            setInactiveWings(response.data.inactive || []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching wings data:", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWingsData();
    }, []);

    const handleAddWing = async (e) => {
        e.preventDefault();
        if (!newWingName.trim()) return;

        setAddingWing(true);
        try {
            await axios.post('nai-members/wings/', { name: newWingName.trim().toUpperCase() });
            setNewWingName('');
            setShowAddModal(false);
            fetchWingsData(); // Refresh list
        } catch (error) {
            console.error("Error adding wing:", error);
            alert("Failed to add wing. It might already exist.");
        } finally {
            setAddingWing(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading wings status...</div>;

    if (selectedWing) {
        return (
            <div>
                 <button onClick={() => setSelectedWing(null)} className="flex items-center text-gray-600 hover:text-gray-900 mb-6">
                    <MdArrowBack className="mr-2" /> Back to Wings
                </button>
                <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Wing: {selectedWing}</h2>
                    <p className="text-sm text-gray-500">Showing all leaders in this wing.</p>
                </div>
                <MembersList 
                    filterType="Leader" 
                    preSelectedWing={selectedWing}
                    onEditClick={onEditClick}
                    onMemberClick={onMemberClick}
                />
            </div>
        );
    }

    const filteredActiveWings = activeWings.filter(wing => wing.toLowerCase().includes(searchQuery.toLowerCase()));
    const filteredInactiveWings = inactiveWings.filter(wing => wing.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h2 className="text-xl font-bold text-gray-800">All Wings</h2>
                
                <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto items-center">
                    <button 
                        onClick={() => setShowAddModal(true)}
                        className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors shadow-sm font-medium"
                    >
                        Add Wings
                    </button>
                    <div className="relative w-full md:w-64">
                        <input 
                            type="text" 
                            placeholder="Search Wings..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2 text-sm"
                        />
                    </div>
                </div>
            </div>
            
            <section className="mb-8">
                <h3 className="text-lg font-semibold text-green-700 mb-4 border-b pb-2">Active Wings ({filteredActiveWings.length})</h3>
                {filteredActiveWings.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredActiveWings.map((wing, index) => (
                            <button 
                                key={index} 
                                onClick={() => setSelectedWing(wing)}
                                className="bg-blue-50 hover:bg-blue-100 text-blue-800 text-sm font-medium py-3 px-4 rounded shadow-sm text-left transition duration-150 ease-in-out border border-blue-200"
                            >
                                {wing}
                            </button>
                        ))}
                    </div>
                ) : (
                     <p className="text-gray-500 text-sm italic">No active wings match your search.</p>
                )}
            </section>

            <section>
                <h3 className="text-lg font-semibold text-red-700 mb-4 border-b pb-2">Inactive Wings ({filteredInactiveWings.length})</h3>
                {filteredInactiveWings.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredInactiveWings.map((wing, index) => (
                            <button 
                                key={index} 
                                className="bg-gray-50 text-gray-400 text-sm font-medium py-3 px-4 rounded shadow-sm text-left cursor-not-allowed border border-gray-200"
                                disabled
                            >
                                {wing}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm italic">No inactive wings match your search.</p>
                )}
            </section>

            {/* Add Wing Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                    <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg leading-6 font-bold text-gray-900 mb-4">Add New Wing</h3>
                            <form onSubmit={handleAddWing}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Wing Name</label>
                                    <input 
                                        type="text"
                                        required
                                        value={newWingName}
                                        onChange={(e) => setNewWingName(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                                        placeholder="e.g. YOUTH WING"
                                        autoFocus
                                    />
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <button 
                                        type="button"
                                        onClick={() => setShowAddModal(false)}
                                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        disabled={addingWing}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium shadow-sm flex items-center"
                                    >
                                        {addingWing ? 'Adding...' : 'Add Wing'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Wings;
